import { yupResolver } from '@hookform/resolvers/yup'
import { Typography } from '@/app/components/Typography'
import { useEffect, useRef, useState } from 'react'
import * as yup from 'yup'

import { Controller, FormProvider, useForm } from 'react-hook-form'

import { ErrorMessage } from '@/app/components/ErrorMessage'
import {
  INotificationStatus,
  useNotificationContext,
} from '@/app/components/Notification'
import { PrimaryButton } from '@/app/components/PrimaryButton'
import { Headline2 } from '@/app/components/Typography'
import { useUserProfile } from '@/app/hooks/data-hooks/useUserProfile'
import { useUserProfileMutation } from '@/app/hooks/data-hooks/useUserProfileMutation'
import { useWorkAreaTaskSuggestions } from '@/app/hooks/data-hooks/useWorkAreaTaskSuggestions'
import WarningAmber from '@mui/icons-material/WarningAmber'
import { ProgressBar } from '@/app/modules/shared/components/progress-bar'
import { useNamespacedIntl } from '../lib/useNamespacedIntl'

export type UserCoreTasksStepValues = {
  workAreaCoreTasks: string[]
  workAreaCoreTasksNotes: string
}

type UserCoreTasksStepProps = {
  onContinue: () => void | Promise<void>
  maxSteps: number
  currentStep: number
}

const TASK_SUGGESTION_SKELETON_COUNT = 10
const TASK_SUGGESTION_SKELETON_WIDTHS = [
  72, 96, 84, 100, 88, 76, 92, 80, 104, 86,
]

function TaskSuggestionSkeletonGrid({
  loadingLabel,
}: {
  loadingLabel: string
}) {
  return (
    <div
      className="flex flex-wrap gap-2 md:gap-3"
      aria-busy="true"
      role="status"
      aria-label={loadingLabel}
    >
      {Array.from({ length: TASK_SUGGESTION_SKELETON_COUNT }).map((_, i) => (
        <div
          key={i}
          className="inline-flex items-center gap-2 rounded-full border border-lines bg-[#F5F6F8] px-3 py-2"
        >
          <div className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-[#D7D9DE]" />
          <div
            className="h-3.5 animate-pulse rounded bg-[#D7D9DE]"
            style={{
              width:
                TASK_SUGGESTION_SKELETON_WIDTHS[
                  i % TASK_SUGGESTION_SKELETON_WIDTHS.length
                ],
            }}
          />
        </div>
      ))}
    </div>
  )
}

function TaskSuggestionError({ errorMessage }: { errorMessage: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-error bg-error-light px-3 py-3 text-sm font-normal text-error">
      <WarningAmber className="h-5 w-5 shrink-0 text-error" aria-hidden />
      {errorMessage}
    </div>
  )
}

export function UserCoreTasksStep({
  onContinue,
  maxSteps,
  currentStep,
}: UserCoreTasksStepProps) {
  const t = useNamespacedIntl('UserOnboarding')
  const { setMessage } = useNotificationContext()
  const { data: { data: userProfile } = {} } = useUserProfile()
  const { mutateAsync: updateProfile, isLoading: isMutating } =
    useUserProfileMutation(userProfile?._id as string)

  const profileId = userProfile?._id

  const {
    data: { data: suggestions = [] } = {},
    isLoading: isLoadingSuggestions,
    isError: isSuggestionsError,
  } = useWorkAreaTaskSuggestions(profileId)

  const [showGeneratingMessage, setShowGeneratingMessage] = useState(false)

  useEffect(() => {
    if (!isLoadingSuggestions) {
      setShowGeneratingMessage(false)
      return
    }
    const timer = setTimeout(() => setShowGeneratingMessage(true), 3000)
    return () => clearTimeout(timer)
  }, [isLoadingSuggestions])

  const formSchema = yup.object<UserCoreTasksStepValues>().shape({
    workAreaCoreTasks: yup
      .array()
      .of(yup.string().required())
      .default([])
      .test(
        'tasks-or-notes',
        t('validation_core_tasks_or_notes_required'),
        function (value) {
          if ((value?.length ?? 0) > 0) return true
          const notes = (this.parent as UserCoreTasksStepValues)
            .workAreaCoreTasksNotes
          return (notes?.trim().length ?? 0) > 0
        }
      ),
    workAreaCoreTasksNotes: yup.string().max(5000).default(''),
  })

  const methods = useForm<UserCoreTasksStepValues>({
    resolver: yupResolver(formSchema),
    disabled: isMutating,
    values: {
      workAreaCoreTasks: userProfile?.workAreaCoreTasks ?? [],
      workAreaCoreTasksNotes: userProfile?.workAreaCoreTasksNotes ?? '',
    },
  })

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = methods
  const formRef = useRef<HTMLFormElement>(null)
  const selectedTasks = watch('workAreaCoreTasks')

  const toggleTask = (taskId: string) => {
    const current = methods.getValues('workAreaCoreTasks') ?? []
    if (current.includes(taskId)) {
      methods.setValue(
        'workAreaCoreTasks',
        current.filter((id) => id !== taskId)
      )
    } else {
      methods.setValue('workAreaCoreTasks', [...current, taskId])
    }
  }

  const handleOnSubmit = async (data: UserCoreTasksStepValues) => {
    const validIds = new Set(suggestions.map((s) => s._id))
    const workAreaCoreTasks = data.workAreaCoreTasks.filter((id) =>
      validIds.has(id)
    )
    try {
      await updateProfile({
        workAreaCoreTasks,
        workAreaCoreTasksNotes: data.workAreaCoreTasksNotes.trim() || undefined,
      })
      await onContinue()
    } catch {
      setMessage(t('profile_update_failed'), INotificationStatus.ERROR)
    }
  }

  const chipsDisabled = isMutating || !!(profileId && isLoadingSuggestions)

  return (
    <FormProvider {...methods}>
      <form
        className="flex px-6 pb-3 md:px-0 md:py-8"
        onSubmit={handleSubmit(handleOnSubmit)}
        ref={formRef}
      >
        <div className="flex w-full flex-col gap-8">
          <div className="flex flex-col gap-2">
            <Headline2>{t('core_tasks_heading')}</Headline2>
            <p className="text-left text-sm text-primary-light">
              {t('core_tasks_subtitle')}
            </p>
          </div>

          {errors.root?.serverError?.message && (
            <ErrorMessage
              message={errors.root?.serverError?.message}
              className="mt-2 text-left"
            />
          )}

          <div className="flex flex-col gap-3">
            <p className="text-sm font-semibold text-secondary">
              {t('core_tasks_select_label')}
            </p>
            {isSuggestionsError ? (
              <TaskSuggestionError
                errorMessage={t('core_tasks_suggestions_error')}
              />
            ) : isLoadingSuggestions ? (
              <>
                {showGeneratingMessage && (
                  <div className="inline-flex items-center gap-2 self-start rounded-full bg-mydra-gray px-2 py-1">
                    <span
                      className="h-2 w-2 shrink-0 rounded-full bg-mydra-purple"
                      aria-hidden
                    />
                    <Typography
                      variant="body2"
                      className="font-medium text-mydra-purple"
                    >
                      {t('core_tasks_generating_message')}
                    </Typography>
                  </div>
                )}
                <TaskSuggestionSkeletonGrid
                  loadingLabel={t('loading_options')}
                />
              </>
            ) : suggestions.length === 0 ? (
              <p className="text-sm text-gray-500">{t('core_tasks_empty')}</p>
            ) : (
              <Controller
                control={control}
                name="workAreaCoreTasks"
                render={() => (
                  <div className="flex flex-wrap gap-2 md:gap-3">
                    {suggestions.map((task) => {
                      const selected =
                        selectedTasks?.includes(task._id) ?? false
                      return (
                        <button
                          key={task._id}
                          type="button"
                          disabled={chipsDisabled}
                          onClick={() => toggleTask(task._id)}
                          aria-pressed={selected}
                          className={`inline-flex w-max items-center gap-2 rounded-full border px-3 py-2 text-left text-sm font-medium transition-all ${
                            selected
                              ? 'border-mydra-purple bg-mydra-purple-light text-mydra-purple'
                              : 'border-gray-200 bg-white text-mydra-black'
                          } ${chipsDisabled ? 'cursor-not-allowed opacity-50' : ''}`}
                        >
                          <span
                            className={`h-2 w-2 shrink-0 rounded-full ${
                              selected ? 'bg-mydra-purple' : 'bg-mydra-black'
                            }`}
                            aria-hidden
                          />
                          {task.name}
                        </button>
                      )
                    })}
                  </div>
                )}
              />
            )}
          </div>

          {errors.workAreaCoreTasks?.message && (
            <ErrorMessage
              message={errors.workAreaCoreTasks.message}
              className="text-left"
            />
          )}

          <div className="flex flex-col gap-2">
            <label
              htmlFor="workAreaCoreTasksNotes"
              className="text-sm font-semibold text-secondary"
            >
              {t('core_tasks_notes_label')}
            </label>
            <Controller
              control={control}
              name="workAreaCoreTasksNotes"
              render={({ field }) => (
                <textarea
                  {...field}
                  id="workAreaCoreTasksNotes"
                  rows={5}
                  disabled={isMutating}
                  placeholder={t('core_tasks_notes_placeholder')}
                  className="w-full resize-y rounded-lg border-lines bg-white px-4 py-3 text-sm text-mydra-black outline-none placeholder:text-secondary"
                />
              )}
            />
            {errors.workAreaCoreTasksNotes?.message && (
              <ErrorMessage
                message={errors.workAreaCoreTasksNotes.message}
                className="text-left"
              />
            )}
          </div>

          <div className="mt-auto flex w-full md:justify-end">
            <ProgressBar
              max={maxSteps}
              progress={currentStep}
              className="mr-auto self-center md:w-32"
            />
            <PrimaryButton
              type="submit"
              disabled={isMutating || !!(profileId && isLoadingSuggestions)}
              isLoading={isSubmitting}
              className="w-full md:w-auto"
            >
              {t('continue')}
            </PrimaryButton>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}
